import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocale, formatDate } from "@/i18n/format";
import { useEmailPrefs, useSetEmailPrefs, useEmailLogs } from "@/hooks/use-email-prefs";
import { useTelegramLink, useUnlinkTelegram } from "@/hooks/use-telegram-link";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

export function SettingsNotificationsPage() {
  const { t } = useTranslation("settings");
  const locale = useLocale();
  const prefs = useEmailPrefs();
  const mutate = useSetEmailPrefs();
  const [showLogs, setShowLogs] = useState(false);
  const logs = useEmailLogs();
  const tg = useTelegramLink();
  const unlink = useUnlinkTelegram();

  return (
    <div className="px-6 py-8 max-w-7xl mx-auto w-full">
      <h1 className="mb-6 text-2xl font-semibold">{t("notifications.title")}</h1>
      <Card>
        <CardHeader>
          <CardTitle>{t("notifications.email.sectionTitle")}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium">{t("notifications.email.transactionalTitle")}</p>
              <p className="text-sm text-muted-foreground">
                {t("notifications.email.transactionalDescription")}
              </p>
            </div>
            <Switch
              checked={prefs.data?.emailNotifications ?? true}
              disabled={prefs.isLoading || mutate.isPending}
              onCheckedChange={(v) => mutate.mutate(v)}
            />
          </div>
          <Button variant="link" className="px-0" onClick={() => setShowLogs((s) => !s)}>
            {showLogs ? t("notifications.email.hideRecentEmails") : t("notifications.email.viewRecentEmails")}
          </Button>
          {showLogs ? (
            <div className="space-y-2">
              {logs.isLoading ? <p className="text-sm">{t("notifications.email.loading")}</p> : null}
              {logs.data?.map((l) => (
                <div key={l.id} className="flex items-center justify-between rounded border p-2 text-sm">
                  <div>
                    <span className="font-mono">{l.eventType}</span>
                    <span className="ml-2 text-muted-foreground">
                      {formatDate(new Date(l.sentAt), locale, { dateStyle: "medium", timeStyle: "short" })}
                    </span>
                  </div>
                  <Badge variant={l.status === "sent" ? "default" : "secondary"}>{l.status}</Badge>
                </div>
              ))}
              {logs.data?.length === 0 ? (
                <p className="text-sm text-muted-foreground">{t("notifications.email.noEmailsYet")}</p>
              ) : null}
            </div>
          ) : null}
        </CardContent>
      </Card>
      <Card className="mt-4">
        <CardHeader>
          <CardTitle>{t("notifications.telegram.sectionTitle")}</CardTitle>
        </CardHeader>
        <CardContent>
          {tg.data?.linked ? (
            <div className="flex items-center justify-between">
              <p>{t("notifications.telegram.linked")}<code>{tg.data.chatId}</code></p>
              <Button variant="outline" onClick={() => unlink.mutate()} disabled={unlink.isPending}>
                {t("notifications.telegram.disconnect")}
              </Button>
            </div>
          ) : (
            <div>
              <p className="text-sm text-muted-foreground">
                {t("notifications.telegram.connectInstructions")}
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
